if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
    }

    get productData() {
      const productId = Number(this.dataset.productId);
      return window?._themeProducts?.[productId] || null;
    }

    get productInfo() {
      return this.closest("product-info");
    }

    get preserveEmptySelectionState() {
      const disableSelectedVariantDefault =
        this.productInfo?.dataset?.disableSelectedVariantDefault === "true";
      const urlParams = new URLSearchParams(window.location.search);

      return disableSelectedVariantDefault && !urlParams.has("variant");
    }

    get unavailableLabelText() {
      return this.dataset.unavailableLabel || "";
    }

    get optionGroups() {
      return Array.from(this.querySelectorAll(".m-product-option__inner")).map(
        (group, index) => {
          const position = index + 1;
          const select = group.querySelector("select");

          if (select) {
            const options = Array.from(select.options);
            const selectedOption =
              options.find((option) => option.selected && !option.disabled) ||
              options.find((option) => !option.disabled) ||
              null;

            return {
              position,
              group,
              options,
              value: selectedOption?.value || "",
              optionValueId: selectedOption?.dataset.optionValueId || "",
            };
          }

          const inputs = Array.from(group.querySelectorAll('input[type="radio"]'));
          const selectedInput =
            inputs.find((input) => input.checked) ||
            inputs.find((input) => !input.disabled) ||
            null;

          return {
            position,
            group,
            inputs,
            value: selectedInput?.value || "",
            optionValueId: selectedInput?.dataset.optionValueId || "",
          };
        }
      );
    }

    get selectedOptionValues() {
      return this.optionGroups
        .map(({ optionValueId }) => optionValueId)
        .filter(Boolean);
    }

    connectedCallback() {
      this.addEventListener('change', (event) => {
        const target = this.getInputForEventTarget(event.target);
        const { variant, selectedOptionValues, shouldSyncImmediately } =
          this.getSelectedOptionValuesForTarget(target);

        if (variant && shouldSyncImmediately) {
          this.syncVariantSelection(variant);
        }

        MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, {
          data: { selectedOptionValues, event, target }
        });
      });

      queueMicrotask(() => {
        this.ensureValidSelection();
      });
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }

    get selectedVariant() {
      const selectedVariantNode = this.querySelector("[data-selected-variant]");
      if (!selectedVariantNode) return null;

      try {
        return JSON.parse(selectedVariantNode.textContent);
      } catch (error) {
        return null;
      }
    }

    getSelectedOptionValuesForTarget(target) {
      const targetProductUrl = target?.dataset?.productUrl;

      if (targetProductUrl && targetProductUrl !== this.dataset.productUrl) {
        return {
          variant: null,
          selectedOptionValues: this.selectedOptionValues,
          shouldSyncImmediately: false,
        };
      }

      const variant = this.resolveVariantForTarget(target);

      return {
        variant,
        selectedOptionValues:
          (variant && this.getOptionValueIdsFromVariant(this.optionGroups, variant)) ||
          this.selectedOptionValues,
        shouldSyncImmediately: !!variant,
      };
    }

    ensureValidSelection() {
      const productData = this.productData;
      const optionGroups = this.optionGroups;

      if (!productData?.variants?.length || optionGroups.length <= 0) {
        return;
      }

      if (this.preserveEmptySelectionState && !this.hasAnyExplicitSelection(optionGroups)) {
        return;
      }

      const selectedValues = optionGroups.reduce((result, group) => {
        result[group.position] = group.value;
        return result;
      }, {});

      const groupsWithoutSelection = optionGroups.filter(
        (group) => !this.hasExplicitSelection(group)
      );

      const normalizedVariant = this.getNormalizedVariant(selectedValues);

      if (!normalizedVariant) return;

      this.syncVisibleOptions(normalizedVariant, optionGroups);

      const selectedVariant = this.selectedVariant;
      const shouldNormalize =
        !selectedVariant ||
        selectedVariant.id !== normalizedVariant.id ||
        groupsWithoutSelection.length > 0;

      if (!shouldNormalize) return;

      this.syncVariantSelection(normalizedVariant, optionGroups);

      const triggerField = this.getFieldForGroupValue(
        optionGroups[0],
        normalizedVariant.option1
      );
      const dispatchField =
        triggerField ||
        this.getFieldForGroupValue(optionGroups[0], normalizedVariant.option1);

      if (!dispatchField) return;

      queueMicrotask(() => {
        dispatchField.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    hasExplicitSelection(group) {
      if (group.options) {
        return group.options.some((option) => option.selected && !option.disabled);
      }

      return group.inputs.some((input) => input.checked && !input.disabled);
    }

    hasAnyExplicitSelection(optionGroups = this.optionGroups) {
      return optionGroups.some((group) => this.hasExplicitSelection(group));
    }

    applyVariantSelection(variant, optionGroups = this.optionGroups) {
      let triggerField = null;

      optionGroups.forEach((group) => {
        const variantValue = variant[`option${group.position}`];
        const matchedField = this.getFieldForGroupValue(group, variantValue);

        if (!matchedField) return;

        if (group.options) {
          group.options.forEach((option) => {
            option.selected = option === matchedField;
          });

          const select = group.group.querySelector("select");
          if (select && select.value !== matchedField.value) {
            select.value = matchedField.value;
            triggerField = triggerField || select;
          } else if (select && !triggerField) {
            triggerField = select;
          }

          return;
        }

        group.inputs.forEach((input) => {
          const shouldCheck = input === matchedField;
          if (input.checked !== shouldCheck) {
            input.checked = shouldCheck;
            if (shouldCheck) {
              triggerField = triggerField || input;
            }
          }
        });

        if (!triggerField) {
          triggerField = matchedField;
        }
      });

      return triggerField;
    }

    getFieldForGroupValue(group, value) {
      if (!group || !value) return null;

      if (group.options) {
        return group.options.find((option) => option.value === value) || null;
      }

      return group.inputs.find((input) => input.value === value) || null;
    }

    syncUnavailableLabel(label, isUnavailable) {
      if (!label) return;

      const existingUnavailableLabel = label.querySelector(".label-unavailable");

      if (!isUnavailable) {
        existingUnavailableLabel?.remove();
        return;
      }

      if (existingUnavailableLabel || !this.unavailableLabelText) return;

      const unavailableLabel = document.createElement("span");
      unavailableLabel.className = "m:visually-hidden label-unavailable";
      unavailableLabel.textContent = this.unavailableLabelText;
      label.append(unavailableLabel);
    }

    syncVariantSelection(variant, optionGroups = this.optionGroups) {
      this.applyVariantSelection(variant, optionGroups);
      this.syncVisibleOptions(variant, optionGroups);
      this.syncSelectedValueLabels(variant, optionGroups);
      this.updateSelectedVariantState(variant);
      this.updateVariantIdInputs(variant?.id);
    }

    syncVisibleOptions(variant, optionGroups = this.optionGroups) {
      optionGroups.forEach((group) => {
        const optionState = this.getOptionStateForGroup(group.position, variant);
        this.updateGroupVisibility(group, optionState);
      });
    }

    getOptionStateForGroup(position, variant) {
      const productData = this.productData;
      if (!productData?.variants?.length) {
        return {
          visibleValues: new Set(),
          availableValues: new Set(),
        };
      }

      if (position === 1) {
        return {
          visibleValues: new Set(
            productData.variants
              .map((item) => item.option1)
              .filter(Boolean)
          ),
          availableValues: new Set(
            productData.variants
              .filter((item) => item.available)
              .map((item) => item.option1)
              .filter(Boolean)
          ),
        };
      }

      const selectedPrefix = {};
      for (let index = 1; index < position; index += 1) {
        selectedPrefix[index] = variant?.[`option${index}`];
      }

      const matchingVariants = this.getVariantsMatchingPrefix(selectedPrefix, false);
      const availableVariants = this.getVariantsMatchingPrefix(selectedPrefix, true);

      return {
        visibleValues: new Set(
          matchingVariants
            .map((item) => item[`option${position}`])
            .filter(Boolean)
        ),
        availableValues: new Set(
          availableVariants
            .map((item) => item[`option${position}`])
            .filter(Boolean)
        ),
      };
    }

    getVariantsMatchingPrefix(selectedPrefix = {}, availableOnly = false) {
      const productData = this.productData;
      if (!productData?.variants?.length) return [];

      const sourceVariants = availableOnly
        ? productData.variants.filter((variant) => variant.available)
        : productData.variants;

      const matches = sourceVariants.filter((variant) =>
        Object.entries(selectedPrefix).every(([position, value]) => {
          if (!value) return true;
          return variant[`option${position}`] === value;
        })
      );

      if (matches.length > 0 || availableOnly) {
        return matches;
      }

      return this.getVariantsMatchingPrefix(selectedPrefix, true);
    }

    updateGroupVisibility(
      group,
      { visibleValues = new Set(), availableValues = new Set() } = {}
    ) {
      if (group.options) {
        let visibleCount = 0;

        group.options.forEach((option) => {
          const isVisible = visibleValues.has(option.value);
          const isAvailable = availableValues.has(option.value);

          option.hidden = !isVisible;
          option.disabled = !isVisible || !isAvailable;
          if (isVisible) visibleCount += 1;
        });

        group.group.closest(".m-product-option")?.classList.toggle("m:hidden", visibleCount === 0);
        return;
      }

      let visibleCount = 0;

      group.inputs.forEach((input) => {
        const isVisible = visibleValues.has(input.value);
        const isAvailable = availableValues.has(input.value);
        const isUnavailable = isVisible && !isAvailable;
        const optionNode = input.closest(".m-product-option--node");
        const optionLabel =
          input.nextElementSibling?.tagName === "LABEL"
            ? input.nextElementSibling
            : optionNode?.querySelector(".m-product-option--node__label");

        input.disabled = !isVisible || !isAvailable;
        input.classList.toggle("disabled", !isVisible || !isAvailable);
        optionNode?.classList.toggle("m:hidden", !isVisible);
        optionNode?.classList.toggle("m-product-option--node__unavailable", isUnavailable);
        this.syncUnavailableLabel(optionLabel, isUnavailable);

        if (isVisible) visibleCount += 1;
      });

      group.group.closest(".m-product-option")?.classList.toggle("m:hidden", visibleCount === 0);
    }

    syncSelectedValueLabels(variant, optionGroups = this.optionGroups) {
      optionGroups.forEach((group) => {
        const variantValue = variant?.[`option${group.position}`] || "";

        group.group.dataset.selectedValue = variantValue;

        const selectedLabel = group.group.querySelector(".option-label--selected");
        if (selectedLabel) {
          selectedLabel.textContent = variantValue;
        }
      });
    }

    updateSelectedVariantState(variant) {
      const selectedVariantNode = this.querySelector("[data-selected-variant]");
      if (!selectedVariantNode) return;

      selectedVariantNode.textContent = JSON.stringify(variant || null);
    }

    updateVariantIdInputs(variantId) {
      const productInfo = this.closest("product-info");
      if (!productInfo) return;

      productInfo
        .querySelectorAll('form input[name="id"]')
        .forEach((input) => {
          input.value = variantId || "";
        });
    }

    resolveVariantForTarget(target) {
      const productData = this.productData;
      const optionGroups = this.optionGroups;

      if (!productData?.variants?.length || optionGroups.length <= 0) {
        return null;
      }

      const selectedValues = this.getSelectedValuesMap(optionGroups);
      const changedPosition = this.getOptionPosition(target);

      return this.getNormalizedVariant(selectedValues, changedPosition);
    }

    getSelectedValuesMap(optionGroups = this.optionGroups) {
      return optionGroups.reduce((result, group) => {
        result[group.position] = group.value;
        return result;
      }, {});
    }

    getNormalizedVariant(selectedValues, changedPosition = null) {
      const productData = this.productData;
      if (!productData?.variants?.length) return null;

      return (
        this.findMatchingVariant(productData.variants, selectedValues) ||
        this.findClosestVariant(productData.variants, selectedValues, changedPosition)
      );
    }

    findMatchingVariant(variants, selectedValues) {
      return (
        variants.find((variant) =>
          Object.entries(selectedValues).every(([position, value]) => {
            if (!value) return true;
            return variant[`option${position}`] === value;
          })
        ) || null
      );
    }

    findClosestVariant(variants, selectedValues, changedPosition) {
      const variantPools = [
        variants.filter((variant) => variant.available),
        variants,
      ].filter((pool) => pool.length > 0);

      for (const pool of variantPools) {
        const changedValue = changedPosition && selectedValues[changedPosition];
        const matchingChangedVariants =
          changedValue
            ? pool.filter(
                (variant) =>
                  variant[`option${changedPosition}`] === changedValue
              )
            : pool;
        const candidatePool = matchingChangedVariants.length
          ? matchingChangedVariants
          : pool;

        const closestVariant = candidatePool
          .map((variant, index) => ({
            variant,
            index,
            score: this.getVariantMatchScore(
              variant,
              selectedValues,
              changedPosition
            ),
          }))
          .sort((first, second) => second.score - first.score || first.index - second.index)[0];

        if (closestVariant?.variant) {
          return closestVariant.variant;
        }
      }

      return null;
    }

    getVariantMatchScore(variant, selectedValues, changedPosition) {
      return Object.entries(selectedValues).reduce((score, [position, value]) => {
        if (!value) return score;

        if (variant[`option${position}`] === value) {
          return score + (Number(position) === changedPosition ? 100 : 1);
        }

        return score;
      }, 0);
    }

    getOptionValueIdsFromVariant(optionGroups, variant) {
      return optionGroups
        .map((group) => {
          const variantValue = variant[`option${group.position}`];

          if (!variantValue) return null;

          if (group.options) {
            return (
              group.options.find((option) => option.value === variantValue)
                ?.dataset.optionValueId || null
            );
          }

          return (
            group.inputs.find((input) => input.value === variantValue)
              ?.dataset.optionValueId || null
          );
        })
        .filter(Boolean);
    }

    getOptionPosition(target) {
      const optionNode = target?.closest?.(".m-product-option--node");
      if (optionNode?.dataset?.optionPosition) {
        return Number(optionNode.dataset.optionPosition);
      }

      const group = target?.closest?.(".m-product-option__inner");
      if (!group) return null;

      const groupIndex = Array.from(
        this.querySelectorAll(".m-product-option__inner")
      ).indexOf(group);

      return groupIndex >= 0 ? groupIndex + 1 : null;
    }
  }
  customElements.define("variant-picker", VariantPicker);
}
